'use client';

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { bravemoveTokens } from '../bravemoveTokens';
import type { PaletteKey } from '../tokens/types';
import { ThemeContext, type ThemeContextValue } from './ThemeContext';

export interface BraveMoveThemeProviderProps {
  readonly initial?: PaletteKey;
  readonly children: ReactNode;
  /**
   * When true (default on web), syncs `data-theme` to `document.documentElement`
   * so CSS variable overrides take effect. Set false on React Native or in
   * environments without a DOM.
   */
  readonly syncDocument?: boolean;
}

// Cast through globalThis so this file type-checks under both web tsconfigs
// (which include "dom" lib) and the mobile tsconfig (which deliberately does
// not). At runtime, RN's globalThis simply has no `document`.
type GlobalWithDocument = typeof globalThis & {
  document?: {
    documentElement: {
      setAttribute(name: string, value: string): void;
      style?: { colorScheme: string };
    } | null;
  };
};

function getDomDocument(): GlobalWithDocument['document'] {
  return (globalThis as GlobalWithDocument).document;
}

export function BraveMoveThemeProvider({
  initial = 'garnet_light',
  children,
  syncDocument,
}: BraveMoveThemeProviderProps) {
  const [palette, setPaletteState] = useState<PaletteKey>(initial);

  const shouldSync = syncDocument ?? getDomDocument() != null;

  useEffect(() => {
    if (!shouldSync) return;
    const doc = getDomDocument();
    doc?.documentElement?.setAttribute('data-theme', palette);
    if (doc?.documentElement?.style) {
      doc.documentElement.style.colorScheme = palette.endsWith('_dark') ? 'dark' : 'light';
    }
  }, [palette, shouldSync]);

  const setPalette = useCallback((next: PaletteKey) => {
    setPaletteState(next);
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({
      tokens: bravemoveTokens(palette),
      palette,
      setPalette,
    }),
    [palette, setPalette],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}
